import { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { Loading, Step, StepLabel, Stepper } from "../../components";
import { InputErros } from "../../helpers";
import { Minimal } from "../../layouts";
import { ImportarPessoasActions, ImportarPessoasStepper } from "./components";
import { Pessoas } from "./entities";
import {
  history,
  EnderecoService,
  notification,
  PessoaService,
  TiposPessoasService,
} from "../../services";
import { cacheApi, responseErros } from "../../utils";
import { useCacheContext } from "../../contexts/cache.context";
import pessoasCsvValidator from "./middlewares/pessoas-csv.validator";

const pessoaService = new PessoaService();
const tiposPessoasService = new TiposPessoasService();
const enderecoService = new EnderecoService();

const ImportarPessoasView = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [arquivoCsv, setArquivoCsv] = useState(null);
  const [dadosCsv, setDadosCsv] = useState(null);
  const [tiposPessoa, setTiposPessoa] = useState([]);
  const [estados, setEstados] = useState([]);
  const [isCadastro, setIsCadastro] = useState(false);
  const [loading, setLoading] = useState(false);
  const { cacheDto, setCacheDto } = useCacheContext();

  const [inputErro, setInputErro] = useState([]);
  const inputErros = new InputErros(inputErro, setInputErro);

  const steps = [
    { titulo: "Selecionar Arquivo", funcaoAcao: buscarXlsxData },
    { titulo: "Clientes", funcaoAcao: handleSubmit },
  ];

  async function buscarTiposPessoa() {
    const result = await tiposPessoasService.getAll();
    if (!result.isAxiosError) {
      const formatResult = result.data.map(({ id, descricao }) => ({
        id,
        descricao: descricao.toUpperCase(),
      }));
      setTiposPessoa(formatResult);
    } else {
      responseErros(result);
    }
  }

  const buscaEstados = async () => {
    const cacheBody = {
      timeMinutes: 60,
      apiFunction: enderecoService.getEstados(),
      dto: { cacheDto: cacheDto, setCacheDto: setCacheDto },
    };
    const res = await cacheApi(`${enderecoService.urlBase}/estados`, cacheBody);
    if (!res.isAxiosError) {
      setEstados(res.data);
    } else {
      responseErros(res);
    }
  };

  useEffect(() => {
    buscarTiposPessoa();
    buscaEstados();
  }, []);

  async function buscarXlsxData() {
    try {
      if (!arquivoCsv) {
        throw new Error(
          "Por favor, inserir CSV/XLS referente a importação de produtos",
        );
      }
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = async (e) => {
          try {
            await handleFileRead(e);
            resolve();
          } catch (error) {
            reject(error);
          }
        };
        reader.onerror = () =>
          reject(new Error("Erro ao ler o arquivo CSV/XLS"));
        reader.readAsArrayBuffer(arquivoCsv);
      });
    } catch (error) {
      throw new Error(error.message);
    }
  }

  async function formatarCidadesEndereco(dados) {
    const cidades = new Set();
    dados.forEach((item) => {
      if (item.endereco?.cidade) {
        cidades.add(item.endereco.cidade);
      }
    });
    const result = await enderecoService.getCidadeByNome({ nomes: Array.from(cidades) });
    if (!result.isAxiosError) {
      dados.forEach((pessoa) => {
        const cidade = pessoa.endereco?.cidade;
        if (cidade) {
          const data = result.data.find(({ nome }) => nome === cidade);
          pessoa.endereco.setCidade(data?.id, data?.estadoId);
        }
      });
    } else {
      responseErros(result);
      return false;
    }
  }

  const handleFileRead = async (e) => {
    const csvData = e.target.result;
    const jsonData = extrairDados(csvData);
    try {
      await formatarCidadesEndereco(jsonData);
    } catch (error) {
      throw new Error(error.message);
    }

    setDadosCsv(jsonData);
    setIsCadastro(true);
  };

  function formatarDadosJson(headers, dados) {
    let item = null;
    headers.forEach((header, i) => {
      const value = dados[i];
      if (value !== undefined && value !== null && value !== "") {
        if (!item) item = {};
        item[header] = value;
      }
    });
    return item;
  }

  function extrairDados(csvData) {
    const workbook = XLSX.read(csvData, { type: "buffer" });
    const planilhasValidas = [
      "PESSOA",
      "ENDERECO",
      ...tiposPessoa.map(({ descricao }) => descricao),
    ];
    let dadosPessoas = [],
      planilhas = [];

    for (const nomePlanilha of workbook.SheetNames) {
      if (!planilhasValidas.includes(nomePlanilha)) continue;
      const sheet = workbook.Sheets[nomePlanilha];
      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });
      planilhas.push({
        name: nomePlanilha,
        headers: rows[0],
        rows: rows.slice(1),
      });
    }

    const qtdPessoas = Math.min(500, planilhas[0].rows.length);
    for (let i = 0; i < qtdPessoas; i++) {
      let pessoa = null;
      for (const sheet of planilhas) {
        const row = sheet.rows[i] || [];
        if (row.length) {
          const item = formatarDadosJson(sheet.headers, row);
          if (sheet.name === "PESSOA") {
            pessoa = new Pessoas(item);
            continue;
          } else if (sheet.name === "ENDERECO") {
            pessoa.setEndereco(item);
          } else if (sheet.name === "CLIENTE") {
            pessoa.setCliente(item);
          } else if (sheet.name === "FORNECEDOR") {
            pessoa.setFornecedor(item);
          } else if (sheet.name === "TRANSPORTADORA") {
            pessoa.setTransportadora(item);
          } else if (sheet.name === "COLABORADOR") {
            pessoa.setColaborador(item);
          } else if (sheet.name === "PRESTADOR DE SERVIÇO") {
            pessoa.setPrestadorServico(item);
          } else if (sheet.name === "PROFISSIONAL") {
            pessoa.setProfissional(item);
          }
          if (item) {
            const id = tiposPessoa.find(
              ({ descricao }) => descricao === sheet.name,
            )?.id;
            pessoa.setTiposPessoa(id);
          }
        }
      }
      if (pessoa) {
        dadosPessoas.push(pessoa);
      }
    }

    return dadosPessoas?.length ? dadosPessoas : [];
  }

  async function handleSubmit() {
    pessoasCsvValidator
      .validate(dadosCsv, { abortEarly: false })
      .then(async () => {    
        setLoading(true);
        const result = await pessoaService.importarPessoas(dadosCsv);
        setLoading(false);
        if (!result.isAxiosError) {
          notification.cadastroSucesso();
          history.push("/integracao/pessoas");
        } else {
          responseErros(result);
        }
      })
      .catch((err) => {
        inputErros.set(err);
      });
  }

  const handleInitStep = () => {
    setActiveStep(0);
    setArquivoCsv(null);
    setDadosCsv(null);
    setIsCadastro(false);
    setInputErro([]);
  };

  return (
    <>
      <Minimal
        title="Importação de Clientes"
        setLoading={setLoading}
        setActiveStep={setActiveStep}
        cardAction={
          <ImportarPessoasActions
            steps={steps}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            handleInitStep={handleInitStep}
            isCadastro={isCadastro}
            setIsCadastro={setIsCadastro}
            setLoading={setLoading}
          />
        }
      >
        <Stepper activeStep={activeStep}>
          {steps.map(({ titulo }) => (
            <Step key={titulo}>
              <StepLabel>{titulo}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <ImportarPessoasStepper
          activeStep={activeStep}
          arquivoCsv={arquivoCsv}
          setArquivoCsv={setArquivoCsv}
          dadosCsv={dadosCsv}
          setIsCadastro={setIsCadastro}
          tiposPessoa={tiposPessoa}
          estados={estados}
          inputErros={inputErros}
          setLoading={setLoading}
          enderecoService={enderecoService}
          cacheDto={cacheDto}
          setCacheDto={setCacheDto}
        />
      </Minimal>
      <Loading loading={loading} />
    </>
  );
};

export default ImportarPessoasView;
